/**
 * Centralized Route Constants
 *
 * This file defines all application routes as constants to avoid hardcoded strings.
 * Routes are organized into a single ROUTES object with dynamic route builders.
 *
 * User Types:
 * - learner: Users taking workshops, building CVs (learner_user_profiles table)
 * - employer: Users managing company profiles (employer_user_profiles table)
 * - platform: Internal staff managing platform (platform_user_profiles table)
 *
 * Note: Users can have multiple types (e.g., a platform user who is also a learner)
 */

/**
 * User type literal type
 */
export type UserType = 'learner' | 'employer' | 'platform';

export const ROUTES = {
  // ═══════════════════════════════════════════════════════════════
  // PUBLIC ROUTES
  // ═══════════════════════════════════════════════════════════════
  HOME: '/',
  EMPLOYMENT_JOURNEY: '/employment-journey',
  FOR_EMPLOYERS: '/for-employers',

  // Public Learning Hub (unauthenticated)
  PUBLIC_LEARNING_HUB: '/learning-hub',
  PUBLIC_WORKSHOPS: '/learning-hub/workshops',
  PUBLIC_RESOURCE_LIBRARY: '/learning-hub/resource-library',
  /** @deprecated Use PUBLIC_RESOURCE_LIBRARY_BY_SLUG for SEO-friendly URLs */
  PUBLIC_RESOURCE_LIBRARY_DETAIL: (resourceId: string) =>
    `/learning-hub/resource-library/${resourceId}`,
  /** SEO-friendly slug-based resource URL (Task 168.9-168.11) */
  PUBLIC_RESOURCE_LIBRARY_BY_SLUG: (slug: string) =>
    `/learning-hub/resource-library/${slug}`,

  // Serene Zone Breathing Tools (Public)
  /** @deprecated Tools listing removed - functionality moved to Resource Library Tools tab (Task 156.22) */
  PUBLIC_TOOLS: '/learning-hub/resource-library/tools',
  /** Serene Zone entry point - breathing exercises intro/selection page */
  PUBLIC_TOOLS_BREATHING: '/learning-hub/resource-library/tools/breathing',
  PUBLIC_TOOLS_BREATHING_478: '/learning-hub/resource-library/tools/breathing/478',
  PUBLIC_TOOLS_BREATHING_BOX: '/learning-hub/resource-library/tools/breathing/box',
  PUBLIC_TOOLS_BREATHING_GROUNDING: '/learning-hub/resource-library/tools/breathing/grounding',

  // Network (hybrid structure)
  NETWORK: '/network',
  NETWORK_COMPANIES: '/network/companies',
  NETWORK_COMPANY: (slug: string) => `/network/companies/${slug}`,

  // Shared content
  SHARED_CV: (cvId: string) => `/cv/${cvId}`,
  SHARED_COVER_LETTER: (token: string) => `/cover-letter/${token}`,

  // Auth
  SIGN_IN: '/auth/sign-in',
  SIGN_UP: '/auth/sign-up',
  ACCOUNT_TYPE: '/auth/account-type',
  EMPLOYER_SIGN_UP: '/auth/employer-sign-up',
  JOIN_TEAM: '/auth/join-team',
  VERIFY_EMAIL: '/auth/verify-email',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // Onboarding
  GET_STARTED: '/get-started',
  ONBOARDING_ABOUT: '/get-started/about',
  ONBOARDING_GOALS: '/get-started/goals',
  ONBOARDING_PREFERENCES: '/get-started/preferences',
  ONBOARDING_ACCOUNT: '/get-started/account',

  // Legal
  TERMS: '/terms',
  PRIVACY: '/privacy',
  COOKIES: '/cookies',

  // ═══════════════════════════════════════════════════════════════
  // AUTHENTICATED ROUTES
  // ═══════════════════════════════════════════════════════════════

  // Smart redirect
  DASHBOARD: '/dashboard',

  // Shared settings (all user types)
  SETTINGS: '/settings',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_SECURITY: '/settings/security',
  SETTINGS_ACCESSIBILITY: '/settings/accessibility',
  SETTINGS_PRIVACY: '/settings/privacy',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_COOKIES: '/settings/cookies',

  // ─────────────────────────────────────────────────────────────
  // LEARNER ROUTES
  // ─────────────────────────────────────────────────────────────
  LEARNER_DASHBOARD: '/learner/dashboard',
  LEARNER_LEARNING_HUB: '/learner/learning-hub',
  LEARNER_LEARNING_HUB_WORKSHOPS: '/learner/learning-hub/workshops',
  LEARNER_LEARNING_HUB_RESOURCE_LIBRARY: '/learner/learning-hub/resource-library',
  /** @deprecated Use LEARNER_RESOURCE_LIBRARY_BY_SLUG for SEO-friendly URLs */
  LEARNER_RESOURCE_LIBRARY_DETAIL: (resourceId: string) =>
    `/learner/learning-hub/resource-library/${resourceId}`,
  /** SEO-friendly slug-based resource URL (Task 168.9-168.11) */
  LEARNER_RESOURCE_LIBRARY_BY_SLUG: (slug: string) =>
    `/learner/learning-hub/resource-library/${slug}`,
  LEARNER_WORKSHOP: (id: string) => `/learner/learning-hub/workshops/${id}`,
  /** @deprecated Use LEARNER_WORKSHOP_RESOURCE_BY_SLUG for SEO-friendly URLs */
  LEARNER_WORKSHOP_RESOURCE: (workshopSlug: string, resourceName: string) =>
    `/learner/learning-hub/workshops/${workshopSlug}/resources/${resourceName}`,
  /** SEO-friendly workshop resource URL (Task 183.12) */
  LEARNER_WORKSHOP_RESOURCE_BY_SLUG: (workshopSlug: string, resourceSlug: string) =>
    `/learner/learning-hub/workshops/${workshopSlug}/resource/${resourceSlug}`,
  LEARNER_WORKSHOP_ACTIVITY: (workshopId: string, activityId: string) =>
    `/learner/learning-hub/workshops/${workshopId}/activities/${activityId}`,
  LEARNER_PROGRESS: '/learner/progress',
  LEARNER_SKILLS: '/learner/skills',
  LEARNER_ANALYTICS: '/learner/analytics',

  LEARNER_PORTFOLIO: '/learner/portfolio',
  LEARNER_CV_LIST: '/learner/portfolio/cv',
  LEARNER_CV_NEW: '/learner/portfolio/cv/new',
  LEARNER_CV_BUILDER: (cvId: string) => `/learner/portfolio/cv/${cvId}`,
  LEARNER_CV_SECTION: (cvId: string, section: string) =>
    `/learner/portfolio/cv/${cvId}/${section}`,

  LEARNER_COVER_LETTERS_LIST: '/learner/portfolio/cover-letters',
  LEARNER_COVER_LETTER_NEW: '/learner/portfolio/cover-letters/new',
  LEARNER_COVER_LETTER_EDITOR: (letterId: string) => `/learner/portfolio/cover-letters/${letterId}`,

  LEARNER_NOTES_LIST: '/learner/portfolio/notes',
  LEARNER_NOTE_NEW: '/learner/portfolio/notes/new',
  LEARNER_NOTE_EDITOR: (noteId: string) => `/learner/portfolio/notes/${noteId}`,

  LEARNER_CAREER_PLANNER: '/learner/portfolio/career-planner',
  LEARNER_CAREER_PLANNER_NEW: '/learner/portfolio/career-planner/new',
  LEARNER_CAREER_PLANNER_GOAL: (goalId: string) => `/learner/portfolio/career-planner/${goalId}`,

  LEARNER_BOOKMARKS: '/learner/portfolio/bookmarks',
  LEARNER_BOOKMARKS_COMPANIES: '/learner/portfolio/bookmarks/companies',
  LEARNER_BOOKMARKS_RESOURCES: '/learner/portfolio/bookmarks/resources',
  LEARNER_FILES: '/learner/portfolio/files',

  LEARNER_STAR_EXAMPLES: '/learner/portfolio/interview-prep',

  LEARNER_EMAIL_TEMPLATES_LIST: '/learner/portfolio/email-templates',
  LEARNER_EMAIL_TEMPLATE_NEW: '/learner/portfolio/email-templates/new',
  LEARNER_EMAIL_TEMPLATE_EDITOR: (templateId: string) => `/learner/portfolio/email-templates/${templateId}`,

  // Learner Network (Company Directory with sidebar - Task 165)
  LEARNER_NETWORK: '/learner/network',
  LEARNER_COMPANIES: '/learner/network/companies',
  LEARNER_COMPANY: (slug: string) => `/learner/network/companies/${slug}`,

  /** @deprecated Tools listing removed - functionality moved to Resource Library Tools tab (Task 156.22) */
  LEARNER_TOOLS: '/learner/learning-hub/resource-library/tools',

  // ─────────────────────────────────────────────────────────────
  // EMPLOYER ROUTES
  // ─────────────────────────────────────────────────────────────
  EMPLOYER_DASHBOARD: '/employer/dashboard',
  EMPLOYER_ANALYTICS: '/employer/analytics',
  EMPLOYER_ANALYTICS_ENGAGEMENT: '/employer/analytics/engagement',
  EMPLOYER_COMPANY: '/employer/company',
  EMPLOYER_COMPANY_CREATE: '/employer/company/create',
  EMPLOYER_COMPANY_EDIT: '/employer/company/edit',
  EMPLOYER_COMPANY_PENDING: '/employer/company/pending',
  EMPLOYER_COMPANY_PREVIEW: '/employer/company/preview',
  EMPLOYER_TEAM: '/employer/team',
  EMPLOYER_TEAM_INVITE: '/employer/team/invite',
  EMPLOYER_NETWORK: '/employer/network',

  // ─────────────────────────────────────────────────────────────
  // ADMIN ROUTES
  // ─────────────────────────────────────────────────────────────
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_SETUP: '/admin/dashboard/users/setup',
  ADMIN_EMAIL: '/admin/dashboard/email',
  ADMIN_ANALYTICS: '/admin/dashboard/analytics',

  ADMIN_REPORTS: '/admin/dashboard/reports',
  ADMIN_REPORT_WORKSHOPS: '/admin/dashboard/reports/workshop-performance',
  ADMIN_REPORT_CONTENT: '/admin/dashboard/reports/content-usage',
  ADMIN_REPORT_REGISTRATION: '/admin/dashboard/reports/registration',
  ADMIN_REPORT_USER_ACTIVITY: '/admin/dashboard/reports/user-activity',
  ADMIN_REPORT_ONBOARDING: '/admin/dashboard/reports/onboarding-completion',
  ADMIN_REPORT_AGE_BREAKDOWN: '/admin/dashboard/reports/age-breakdown',

  ADMIN_WORKSHOPS: '/admin/dashboard/workshops',
  ADMIN_WORKSHOP_NEW: '/admin/dashboard/workshops/new',
  ADMIN_WORKSHOP: (id: string) => `/admin/dashboard/workshops/${id}`,
  ADMIN_WORKSHOP_EDIT: (id: string) => `/admin/dashboard/workshops/${id}/edit`,
  ADMIN_WORKSHOP_RESOURCE_NEW: (workshopId: string) =>
    `/admin/dashboard/workshops/${workshopId}/resources/new`,
  ADMIN_WORKSHOP_RESOURCE_EDIT: (workshopId: string, resourceId: string) =>
    `/admin/dashboard/workshops/${workshopId}/resources/${resourceId}/edit`,
  ADMIN_WORKSHOP_ACTIVITY_NEW: (workshopId: string) =>
    `/admin/dashboard/workshops/${workshopId}/activities/new`,
  ADMIN_WORKSHOP_ACTIVITY_EDIT: (workshopId: string, activityId: string) =>
    `/admin/dashboard/workshops/${workshopId}/activities/${activityId}/edit`,

  ADMIN_COMPANIES: '/admin/dashboard/companies',
  ADMIN_COMPANIES_PENDING: '/admin/dashboard/companies/pending',
  ADMIN_COMPANY: (id: string) => `/admin/dashboard/companies/${id}`,
  ADMIN_COMPANY_EDIT: (id: string) => `/admin/dashboard/companies/${id}/edit`,

  ADMIN_USERS: '/admin/dashboard/users',
  ADMIN_USER: (id: string) => `/admin/dashboard/users/${id}`,
  ADMIN_USER_EDIT: (id: string) => `/admin/dashboard/users/${id}/edit`,

  ADMIN_ROLES: '/admin/dashboard/roles',
  ADMIN_ROLE_NEW: '/admin/dashboard/roles/new',
  ADMIN_ROLE_EDIT: (id: string) => `/admin/dashboard/roles/${id}/edit`,

  // ─────────────────────────────────────────────────────────────
  // ADMIN ROUTES - Content Management
  // ─────────────────────────────────────────────────────────────
  ADMIN_CONTENT: '/admin/dashboard/content',
  ADMIN_CONTENT_NEW: '/admin/dashboard/content/new',
  ADMIN_CONTENT_UPLOAD: '/admin/dashboard/content/upload',
  ADMIN_CONTENT_ITEM: (id: string) => `/admin/dashboard/content/${id}`,
  ADMIN_CONTENT_EDIT: (id: string) => `/admin/dashboard/content/${id}/edit`,
  ADMIN_CONTENT_VERSIONS: (id: string) => `/admin/dashboard/content/${id}/versions`,

  // ─────────────────────────────────────────────────────────────
  // ADMIN ROUTES - Training & Documentation
  // ─────────────────────────────────────────────────────────────
  ADMIN_TRAINING: '/admin/dashboard/training',
  ADMIN_TRAINING_SITEMAP: '/admin/dashboard/training/sitemap-flowcharts',
  ADMIN_TRAINING_CONTENT_GUIDE: '/admin/dashboard/training/content-management-guide',
  ADMIN_TRAINING_REPORTING: '/admin/dashboard/training/reporting-guide',
  ADMIN_TRAINING_EMAIL_GUIDE: '/admin/dashboard/training/email-management',
  ADMIN_TRAINING_COMPANY_GUIDE: '/admin/dashboard/training/company-employer-management',
  ADMIN_TRAINING_WORKSHOP_GUIDE: '/admin/dashboard/training/workshop-management',
} as const;

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

/**
 * Get default dashboard for user type
 */
export function getDefaultDashboard(userType: UserType): string {
  switch (userType) {
    case 'platform':
      return ROUTES.ADMIN_DASHBOARD;
    case 'employer':
      return ROUTES.EMPLOYER_DASHBOARD;
    case 'learner':
      return ROUTES.LEARNER_DASHBOARD;
    default:
      return ROUTES.GET_STARTED;
  }
}

/**
 * Get primary user type (priority: platform > employer > learner)
 */
export function getPrimaryUserType(types: UserType[]): UserType | null {
  if (types.includes('platform')) return 'platform';
  if (types.includes('employer')) return 'employer';
  if (types.includes('learner')) return 'learner';
  return null;
}

/**
 * Check if a given path is a public route
 *
 * @param path - The route path to check
 * @returns True if the path is a public route
 */
export function isPublicRoute(path: string): boolean {
  // Remove query parameters and hash
  const cleanPath = path.split('?')[0].split('#')[0];

  // Exact public routes
  const exactPublicRoutes = [
    '/',
    '/employment-journey',
    '/for-employers',
    '/learning-hub',
    '/terms',
    '/privacy',
    '/cookies',
  ];

  // Public route prefixes (routes that start with these patterns)
  const publicPrefixes = [
    '/network',
    '/learning-hub',
    '/auth/',
    '/get-started',
    '/cv/',
    '/cover-letter/',
  ];

  // Check exact matches
  if (exactPublicRoutes.includes(cleanPath)) {
    return true;
  }

  // Check prefix matches
  return publicPrefixes.some((prefix) => cleanPath.startsWith(prefix));
}

/**
 * Extract route prefix from a path
 *
 * @param path - The route path
 * @returns The route prefix (learner, employer, platform) or null
 */
export function getRoutePrefix(path: string): UserType | null {
  const cleanPath = path.split('?')[0].split('#')[0];

  if (cleanPath.startsWith('/learner')) return 'learner';
  if (cleanPath.startsWith('/employer')) return 'employer';
  if (cleanPath.startsWith('/admin')) return 'platform';

  return null;
}
// Route access is handled by the routing system and authorization layer
